import { useEffect, useState } from "react";
import clsx from "clsx";
import type { PipeMetrics, ShiftMetrics, ShiftWindow } from "@tbm/shared";
import { fmtMetres } from "../lib/format";

interface ShiftPanelProps {
  shift: ShiftMetrics;
  timezone: string;
  /** Pipe-activity payload from /api/latest. */
  pipeMetrics?: PipeMetrics;
}

export function ShiftPanel({ shift, timezone, pipeMetrics }: ShiftPanelProps) {
  // Order so the active shift comes first; the inactive (other) follows.
  // - Active shift carries `currentShiftAdvanceM`.
  // - Inactive shift carries `previousShiftAdvanceM`.
  const dayActive = shift.current.kind === "day";
  const dayWindow = dayActive ? shift.current : shift.previous;
  const dayAdvance = dayActive ? shift.currentShiftAdvanceM : shift.previousShiftAdvanceM;
  const nightWindow = dayActive ? shift.previous : shift.current;
  const nightAdvance = dayActive ? shift.previousShiftAdvanceM : shift.currentShiftAdvanceM;

  // Sub-lines: active tile shows pipes-today, previous-shift tile
  // shows pipes-last-shift. While the schedule can't drive the metrics
  // yet (no items in the schedule, no chainage signal), surface that
  // state instead of silent zeros.
  const ribbonReady = pipeMetrics?.ready === true;
  const initialising = pipeMetrics != null && !ribbonReady;
  const activeSub = ribbonReady
    ? pipesSub(pipeMetrics?.pipesToday, "this shift")
    : initialising
      ? "initialising…"
      : undefined;
  const prevSub = ribbonReady
    ? pipesSub(pipeMetrics?.pipesPreviousShift, "last shift")
    : initialising
      ? "initialising…"
      : undefined;

  // When the drive has no separate night shift, both `current` and
  // `previous` are consecutive 24h windows (kind "day"), so the
  // Day/Night framing is meaningless — show plain Current / Previous
  // shift tiles instead.
  const tiles = shift.nightShiftEnabled === false
    ? [
        { title: "CURRENT SHIFT",  window: windowLabel(shift.current,  timezone), value: shift.currentShiftAdvanceM,  subValue: activeSub, bold: true,  glyph: "☀" },
        { title: "PREVIOUS SHIFT", window: windowLabel(shift.previous, timezone), value: shift.previousShiftAdvanceM, subValue: prevSub,   bold: false, glyph: undefined },
      ]
    : dayActive
      ? [
          { title: "DAY SHIFT",   window: windowLabel(dayWindow,   timezone), value: dayAdvance,   subValue: activeSub, bold: true,  glyph: "☀" },
          { title: "NIGHT SHIFT", window: windowLabel(nightWindow, timezone), value: nightAdvance, subValue: prevSub,   bold: false, glyph: "☾" },
        ]
      : [
          { title: "NIGHT SHIFT", window: windowLabel(nightWindow, timezone), value: nightAdvance, subValue: activeSub, bold: true,  glyph: "☾" },
          { title: "DAY SHIFT",   window: windowLabel(dayWindow,   timezone), value: dayAdvance,   subValue: prevSub,   bold: false, glyph: "☀" },
        ];

  return (
    <section className="card relative overflow-hidden">
      {/* Desktop: single row of 4, divide-x renders separators.
       *  Mobile (<lg): the row collapses to a 2x2 grid so each tile keeps
       *  enough width for its title, window label, and value. The `max-lg:`
       *  utilities are no-ops at desktop widths so the desktop layout is
       *  unchanged. Tiles render their own internal borders on mobile
       *  (passed as `mobileBorder`) since `divide-x` doesn't work in a grid. */}
      <div className="flex items-stretch divide-x divide-border1 max-lg:grid max-lg:grid-cols-2 max-lg:divide-x-0">
        {tiles.map((t, idx) => (
          <ShiftTile
            key={t.title}
            title={t.title}
            window={t.window}
            value={fmtMetres(t.value)}
            subValue={t.subValue}
            highlight={t.bold}
            glyph={t.glyph}
            mobileBorder={mobileBorderClass(idx)}
          />
        ))}
        <CurrentPipeTile
          metrics={pipeMetrics}
          mobileBorder={mobileBorderClass(2)}
        />
        <ShiftTile
          title="THIS WEEK"
          window={weekLabel(shift.week.start, shift.week.end, timezone)}
          value={fmtMetres(shift.weekAdvanceM)}
          highlight
          mobileBorder={mobileBorderClass(3)}
        />
      </div>
    </section>
  );
}

function pipesSub(n: number | undefined, suffix: string): string | undefined {
  if (n == null) return undefined;
  // 1 dp fractional ("1.3 pipes today"). Pluralise on 1.0 only — any
  // fractional value reads better as the plural "pipes".
  const rounded = Math.round(n * 10) / 10;
  const label = `${rounded.toFixed(1)} pipe${rounded === 1 ? "" : "s"}`;
  return `${label} ${suffix}`;
}

/** Border classes for the 2x2 mobile grid. Index 0=TL, 1=TR, 2=BL, 3=BR. */
function mobileBorderClass(idx: number): string {
  const right = idx % 2 === 0 ? "max-lg:border-r max-lg:border-border1" : "";
  const bottom = idx < 2 ? "max-lg:border-b max-lg:border-border1" : "";
  return `${right} ${bottom}`.trim();
}

function ShiftTile({
  title,
  window,
  value,
  subValue,
  highlight = false,
  glyph,
  mobileBorder = "",
}: {
  title: string;
  window: string;
  value: string;
  subValue?: string;
  highlight?: boolean;
  glyph?: string;
  /** Empty string on desktop. On mobile, a combination of
   *  `max-lg:border-r max-lg:border-b max-lg:border-border1` to draw
   *  the 2x2 grid's internal lines. */
  mobileBorder?: string;
}) {
  return (
    <div
      className={
        "flex-1 px-5 py-3 flex items-center gap-4 min-w-0 " +
        // Mobile gets tighter padding and smaller gap so the value column
        // doesn't crash into the label column at ~180px tile width.
        "max-lg:px-3 max-lg:py-2.5 max-lg:gap-2 " +
        mobileBorder
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-dim max-lg:tracking-[0.15em]">
          {glyph && <span className="text-wb text-base leading-none max-lg:text-sm">{glyph}</span>}
          <span className="truncate">{title}</span>
        </div>
        <div className="mono-nums text-[11px] text-muted mt-0.5 truncate max-lg:text-[10px]">{window}</div>
      </div>
      <div className="text-right whitespace-nowrap">
        <div
          className={
            highlight
              ? "mono-nums font-semibold text-[24px] max-lg:text-[18px] text-text"
              : "mono-nums font-medium text-[20px] max-lg:text-[16px] text-muted"
          }
        >
          {value}
        </div>
        {subValue && (
          <div className="mono-nums text-[11px] text-muted mt-0.5 max-lg:text-[10px]">
            {subValue}
          </div>
        )}
      </div>
    </div>
  );
}

function CurrentPipeTile({
  metrics,
  mobileBorder,
}: {
  metrics: PipeMetrics | undefined;
  mobileBorder: string;
}) {
  // The elapsed counter ticks locally so the operator sees the seconds
  // advance between /api/latest refreshes (every 10 s).
  const startMs = metrics?.currentPipeStartTs
    ? new Date(metrics.currentPipeStartTs).getTime()
    : null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (startMs == null) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  const initialising = metrics != null && metrics.ready === false;
  // The elapsed counter is driven by currentPipeStartTs alone, not by
  // `ready`. Before the first pipe of a drive is detected the backend
  // anchors it on the drive start date, and the operator should see
  // that clock running rather than a dash.
  const elapsedLabel =
    startMs != null ? formatElapsed(Math.max(0, nowMs - startMs)) : "—";
  const lastLabel = initialising
    ? "—"
    : metrics?.lastPipeDurationMin != null
      ? formatMinutes(metrics.lastPipeDurationMin)
      : "—";
  const idxLabel = initialising
    ? "Since drive start"
    : metrics?.betweenPipes
      ? "Pipe change"
      : metrics?.currentPipeIndex != null
        ? `Pipe #${metrics.currentPipeIndex}`
        : "No pipe change detected";

  return (
    <div
      className={
        "flex-1 px-5 py-3 flex items-center gap-4 min-w-0 " +
        "max-lg:px-3 max-lg:py-2.5 max-lg:gap-2 " +
        mobileBorder
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-dim max-lg:tracking-[0.15em]">
          <span className="text-wb text-base leading-none max-lg:text-sm">⏱</span>
          <span className="truncate">CURRENT PIPE</span>
        </div>
        <div
          className={clsx(
            "mono-nums text-[11px] mt-0.5 truncate max-lg:text-[10px]",
            metrics?.betweenPipes && !initialising ? "text-warn font-semibold" : "text-muted",
          )}
        >
          {idxLabel}
        </div>
      </div>
      <div className="text-right whitespace-nowrap">
        <div className="mono-nums font-semibold text-[20px] max-lg:text-[16px] text-text">
          {elapsedLabel}
        </div>
        <div className="mono-nums text-[11px] text-muted mt-0.5 max-lg:text-[10px]">
          last: {lastLabel}
        </div>
      </div>
    </div>
  );
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatMinutes(min: number): string {
  if (min < 1) return "<1m";
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function windowLabel(w: ShiftWindow, tz: string): string {
  const startStr = new Date(w.start).toLocaleString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const endStr = new Date(w.end).toLocaleString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const dayStr = new Date(w.start).toLocaleString("en-GB", {
    timeZone: tz,
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
  return `${dayStr} · ${startStr} → ${endStr}`;
}

function weekLabel(startIso: string, endIso: string, tz: string): string {
  const s = new Date(startIso);
  const lastDay = new Date(new Date(endIso).getTime() - 24 * 3600 * 1000);
  const sStr = s.toLocaleString("en-GB", {
    timeZone: tz,
    day: "2-digit",
    month: "short",
  });
  const eStr = lastDay.toLocaleString("en-GB", {
    timeZone: tz,
    day: "2-digit",
    month: "short",
  });
  return `${sStr} → ${eStr}`;
}
